import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  User,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  Memory,
  MemoryCreate,
  MemoryUpdate,
  Category,
  CategoryCreate,
  CategoryUpdate,
  APIError,
} from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

class APIClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Add response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<APIError>) => {
        if (error.response?.status === 401) {
          // Token expired, try to refresh
          const refreshToken = localStorage.getItem('refresh_token')
          if (refreshToken) {
            try {
              const response = await this.refreshToken(refreshToken)
              localStorage.setItem('access_token', response.access_token)
              localStorage.setItem('refresh_token', response.refresh_token)
              
              // Retry the original request
              if (error.config) {
                error.config.headers.Authorization = `Bearer ${response.access_token}`
                return this.client.request(error.config)
              }
            } catch (refreshError) {
              // Refresh failed, clear tokens and redirect to login
              this.clearAuth()
              window.location.href = '/login'
            }
          } else {
            this.clearAuth()
            window.location.href = '/login'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // Auth methods
  async register(data: RegisterRequest): Promise<User> {
    const response = await this.client.post<User>('/api/v1/auth/register', data)
    return response.data
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/api/v1/auth/login', data)
    return response.data
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    })
    return response.data
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/api/v1/auth/me')
    return response.data
  }

  async logout(): Promise<void> {
    await this.client.post('/api/v1/auth/logout')
    this.clearAuth()
  }

  async updateProfile(data: { username?: string; full_name?: string; email?: string }): Promise<User> {
    const response = await this.client.patch<User>('/api/v1/auth/me', data)
    return response.data
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.client.post('/api/v1/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
  }

  clearAuth(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  // Memory methods
  async getMemories(params?: {
    skip?: number
    limit?: number
    category?: string
    source?: string
    is_long_term?: boolean
    search?: string
  }): Promise<Memory[]> {
    const response = await this.client.get<Memory[]>('/api/v1/memories/', { params })
    return response.data
  }

  async getMemory(id: string): Promise<Memory> {
    const response = await this.client.get<Memory>(`/api/v1/memories/${id}`)
    return response.data
  }

  async createMemory(data: MemoryCreate): Promise<Memory> {
    const response = await this.client.post<Memory>('/api/v1/memories', data)
    return response.data
  }

  async updateMemory(id: string, data: MemoryUpdate): Promise<Memory> {
    const response = await this.client.patch<Memory>(`/api/v1/memories/${id}`, data)
    return response.data
  }

  async deleteMemory(id: string): Promise<void> {
    await this.client.delete(`/api/v1/memories/${id}`)
  }

  async semanticSearch(params: {
    query: string
    limit?: number
    category?: string
    source?: string
  }): Promise<Memory[]> {
    const response = await this.client.get<Memory[]>('/api/v1/memories/search/semantic/', { params })
    return response.data
  }

  // Category methods
  async getCategories(): Promise<Category[]> {
    const response = await this.client.get<Category[]>('/api/v1/categories')
    return response.data
  }

  async getCategory(id: string): Promise<Category> {
    const response = await this.client.get<Category>(`/api/v1/categories/${id}`)
    return response.data
  }

  async createCategory(data: CategoryCreate): Promise<Category> {
    const response = await this.client.post<Category>('/api/v1/categories', data)
    return response.data
  }

  async updateCategory(id: string, data: CategoryUpdate): Promise<Category> {
    const response = await this.client.patch<Category>(`/api/v1/categories/${id}`, data)
    return response.data
  }

  async deleteCategory(id: string): Promise<void> {
    await this.client.delete(`/api/v1/categories/${id}`)
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    const response = await this.client.get('/health')
    return response.data
  }
}

export const api = new APIClient()
export default api
